import Restart16 from '@carbon/icons-react/es/restart/16';
import { Comment } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ApprovalComment as ApprovalCommentType,
  ApprovalRequestTo,
  ApprovalStatus,
} from 'api/approval';
import { ProjectId, User } from 'api/models';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { Avatar } from 'components/atoms/avatar';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { RTE } from 'components/atoms/rte';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import styles from './approval.module.css';

export const ApprovalComment = ({
  user,
  data,
  comment,
  projectId,
}: {
  user?: User;
  data: ApprovalRequestTo;
  comment?: ApprovalCommentType;
  projectId: number;
}) => {
  let content = comment?.content;
  const translatedApprovedText = useTranslation('approvals.approved');
  const translatedChangeRequiredText = useTranslation(
    'approvals.change_required',
  );
  let status;

  switch (data.approvalStatus) {
    case ApprovalStatus.Approved:
      status = <FormattedMessage id="approvals.approved_request" />;
      content = content || translatedApprovedText;
      break;
    case ApprovalStatus.ChangeRequested:
      status = <FormattedMessage id="approvals.requested_change" />;
      content = content || translatedChangeRequiredText;
      break;
    default:
      content = '';
  }

  const isApproved = data.approvalStatus === ApprovalStatus.Approved;

  const contentCx = [
    styles.comment_content,
    isApproved ? styles.approved : null,
  ];

  return (
    <Comment
      className={styles.comment__container}
      content={
        <>
          <div className="caption text-03">{status}</div>
          <div className={styles.comment__wrapper}>
            <div
              className={classNames(styles.comment__icon, {
                [styles.comment__icon_approved]: isApproved,
              })}
            >
              {isApproved ? <ApproveOutline16 /> : <Restart16 />}
            </div>
            <RTE
              key={comment?.commentId}
              readonly
              className={classNames(contentCx)}
              data={content}
              projectId={ProjectId(projectId)}
            />
          </div>
        </>
      }
      avatar={
        user && (
          <Avatar shape="square" name={fullName(user)} src={user.photoUrl} />
        )
      }
      author={user && <strong>{fullName(user)}</strong>}
      datetime={
        <small>
          <HumanisedDate timestamp={moment(data.updatedAt)} />
        </small>
      }
    />
  );
};
